import { get } from 'lodash';

export interface CloseClaimReasonConfig {
  desc: string;
  note: string;
  confirm_pop_up?: string;
  excluded_claim_types?: string[];
  included_claim_types?: string[];
  special_config_users_list?: string;
}

export interface CloseClaimType extends CloseClaimReasonConfig {
  key: string;
}

export interface ClaimForClose {
  id: number;
  type: string;
  incident: {
    cause_of_loss?: string | null;
    total_settlement_amount?: number | null;
  };
  exposures: { is_closed: boolean }[];
}

export interface OrgRequiredFieldsForCloseClaim {
  required_fields: { field: string; message: string }[];
}

// The special-config allowlists hold raw `user.id` values from the backend (integers), while the
// frontend's own user model types `id` as a string, so the comparison below has to admit both.
export type UserId = string | number;
export type OrgAdditionalConfigs = Record<string, UserId[] | undefined>;

// `close_claim_reasons` is always populated by the operational-details endpoint, but
// `prepareOperationalDetails` returns `{}` when the payload is missing (a poisoned cache replaying
// an empty response), so the whole key can be absent at runtime.
export const claimCloseTypes = (
  claimType: string,
  orgAdditionalConfigs: OrgAdditionalConfigs | undefined,
  user: { id: UserId },
  closeClaimReasons: Record<string, CloseClaimReasonConfig> | undefined = {}
): CloseClaimType[] =>
  Object.keys(closeClaimReasons).reduce<CloseClaimType[]>((acc, close_type) => {
    const currOption = closeClaimReasons[close_type];
    if (currOption.excluded_claim_types && currOption.excluded_claim_types.includes(claimType)) {
      return acc;
    } else if (currOption.included_claim_types && !currOption.included_claim_types.includes(claimType)) {
      return acc;
    } else if (
      currOption.special_config_users_list &&
      !(
        orgAdditionalConfigs &&
        orgAdditionalConfigs[currOption.special_config_users_list] &&
        orgAdditionalConfigs[currOption.special_config_users_list]?.includes(user.id)
      )
    ) {
      return acc;
    }

    return acc.concat({ ...currOption, key: close_type });
  }, []);

interface ClaimCloseDisabledReasonsParams {
  claim: ClaimForClose;
  isExposureFinancialOnlyOrg: boolean;
  exposureClosingDisabledReason?: string;
  orgRequiredFieldsForCloseClaim?: OrgRequiredFieldsForCloseClaim;
  isConfigurableClosingReasonEnabled: boolean;
}

/**
 * The org close policy the backend does not re-check on `POST /claims/{id}/close` — every surface
 * that offers a claim close has to run it, or the org's rules are silently skipped.
 */
export const getClaimCloseDisabledReasons = ({
  claim,
  isExposureFinancialOnlyOrg,
  exposureClosingDisabledReason,
  orgRequiredFieldsForCloseClaim,
  isConfigurableClosingReasonEnabled,
}: ClaimCloseDisabledReasonsParams): string[] => {
  const disabledReasons: string[] = [];

  if (claim.type === 'home_claim' && !claim.incident.cause_of_loss) {
    disabledReasons.push('Cause of Loss must be set');
  }

  if (isExposureFinancialOnlyOrg) {
    if (exposureClosingDisabledReason) {
      disabledReasons.push(exposureClosingDisabledReason);
    }
  } else if (claim.exposures.some((exposure) => !exposure.is_closed)) {
    disabledReasons.push('Exposures must be closed');
  }

  if (
    !isConfigurableClosingReasonEnabled &&
    orgRequiredFieldsForCloseClaim &&
    orgRequiredFieldsForCloseClaim.required_fields.length
  ) {
    orgRequiredFieldsForCloseClaim.required_fields.forEach(({ field, message }) => {
      const value = get(claim, field);
      if (value === null || value === undefined || value === '') {
        disabledReasons.push(message);
      }
    });
  }

  return disabledReasons;
};

interface ValidateClaimBeforeCloseParams {
  claim: ClaimForClose;
  closeTypeKey: string;
  isTotalSettlementAmountEnabled: boolean;
}

export const validateClaimBeforeClose = ({
  claim,
  closeTypeKey,
  isTotalSettlementAmountEnabled,
}: ValidateClaimBeforeCloseParams): { isValid: boolean; message?: string } => {
  if (
    isTotalSettlementAmountEnabled &&
    closeTypeKey === 'with_pay_settled' &&
    claim.type === 'gl_claim' &&
    (claim.incident.total_settlement_amount === null || claim.incident.total_settlement_amount === undefined)
  ) {
    return { isValid: false, message: 'Total Settlement Amount is required to close a claim with pay - settled.' };
  }

  return { isValid: true };
};
